const RESIZE_DEBOUNCE_MS = 150;
const MOBILE_SCROLL_RESET_MS = 300;
const DESKTOP_SCROLL_RESET_MS = 200;
const HEADER_SCROLL_BUFFER_MULTIPLIER = 0.5;
const PARTIAL_CONTAINER_BUFFER_RATIO = 0.35;
const PARTIAL_BUTTON_BUFFER_MULTIPLIER = 2;
const VISIBILITY_TOLERANCE_PX = 1;
const LIST_SCROLL_DURATION_MS = 350;

class ProductBenefits extends HTMLElement {
  constructor() {
    super();
    this.lastClickedIndex = 0;
    this.isScrolling = false;
    this.resizeTimeout = null;
  }

  getSubheadingList(wrapperSelector) {
    const wrapper = this.querySelector(wrapperSelector);
    if (!wrapper) {
      return null;
    }
    return wrapper.querySelector('ul.benefit-subheadings-scroll') || wrapper.querySelector('ul');
  }

  findPreviousBenefitButton(button) {
    let previous = button ? button.previousElementSibling : null;
    while (previous && !previous.hasAttribute('data-benefit')) {
      previous = previous.previousElementSibling;
    }
    return previous;
  }

  findNextBenefitButton(button) {
    let next = button ? button.nextElementSibling : null;
    while (next && !next.hasAttribute('data-benefit')) {
      next = next.nextElementSibling;
    }
    return next;
  }

  get isMobile() {
    return window.innerWidth < 1024;
  }

  connectedCallback() {
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-benefit]');
      if (!btn) return;

      // Find the index of the clicked benefit in its list
      let clickedIndex = -1;
      const parentUl = btn.closest('ul');
      if (parentUl) {
        const items = Array.from(parentUl.querySelectorAll('[data-benefit]'));
        clickedIndex = items.indexOf(btn);
      }
      if (clickedIndex === -1) return;
      this.lastClickedIndex = clickedIndex;

      // Always update UI immediately (content and button states)
      const benefitName = btn.dataset.benefit;
      this.toggleBenefitContent(benefitName);
      this.updateButtonStates(benefitName);

      // Handle scrolling with debouncing only for scroll operations
      if (this.isMobile) {
        this.handleMobileScrollWithDebounce(benefitName);
      } else {
        this.handleDesktopScrollWithDebounce(benefitName);
      }
    });

    // Set mobile subheadings top position dynamically based on benefit image height
    this.setMobileSubheadingsPosition();

    // Update position on window resize
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.setMobileSubheadingsPosition();
    }, RESIZE_DEBOUNCE_MS);
  }

  setMobileSubheadingsPosition() {
    if (!this.isMobile) return;

    // Find the currently visible benefit block using data attribute
    const visibleBenefitBlock = this.querySelector('[data-benefit-block][data-active-benefit]');
    const section = this.closest('section');

    if (visibleBenefitBlock && section) {
      const benefitImage = visibleBenefitBlock.querySelector('.benefit-image');
      if (benefitImage) {
        const imageHeight = benefitImage.offsetHeight;
        section.style.setProperty('--mobile-subheadings-top', `${imageHeight}px`);
      }
    }
  }

  // Check if a benefit is partially obscured at the bottom of its container
  checkIfPartiallyObscured(btn, container) {
    if (!container) {
      return false;
    }
    
    const btnRect = btn.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // Check if benefit is cut off at bottom (15px or more hidden - more forgiving for floating point)
    const cutoffAmount = btnRect.bottom - containerRect.bottom;
    const buffer = Math.min(
      containerRect.height * PARTIAL_CONTAINER_BUFFER_RATIO,
      btnRect.height * PARTIAL_BUTTON_BUFFER_MULTIPLIER
    );
    const isObscured = cutoffAmount >= -buffer;
    
    return isObscured;
  }

  // Calculate scroll position to bring benefit to top of container
  calculateScrollToTop(btn, container) {
    if (!container) return 0;
    
    const btnRect = btn.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // Calculate position to bring benefit to top of container
    return btnRect.top - containerRect.top + container.scrollTop;
  }

  // Toggle benefit content visibility
  toggleBenefitContent(benefitName) {
    const benefitDescription = this.querySelector(`[data-benefit-block="${benefitName}"]`);
    if (benefitDescription) {
      this.querySelectorAll('[data-benefit-block]').forEach((description) => {
        description.classList.remove('!tw-flex');
        description.classList.add('tw-hidden');
        description.removeAttribute('data-active-benefit');
      });
      benefitDescription.classList.remove('tw-hidden');
      benefitDescription.classList.add('!tw-flex');
      benefitDescription.setAttribute('data-active-benefit', 'true');
    }
  }

  // Update button states for all benefit buttons
  updateButtonStates(benefitName) {
    // Reset all buttons
    this.querySelectorAll('[data-benefit]').forEach((button) => {
      const plus = button.querySelector('[data-plus]');
      const minus = button.querySelector('[data-minus]');
      const highlight = button.querySelector('[data-highlight]');
      button.classList.remove('tw-font-semibold');
      if (plus) {
        plus.classList.remove('!tw-hidden');
      }
      if (minus) {
        minus.classList.remove('!tw-block');
      }
      if (highlight) {
        highlight.classList.remove('!tw-block');
      }
    });

    // Set active state for clicked benefit
    const sameBenefitSubheadings = Array.from(this.querySelectorAll('[data-benefit]')).filter(
      (sh) => sh.dataset.benefit === benefitName
    );
    sameBenefitSubheadings.forEach((benefitBtn) => {
      const plus = benefitBtn.querySelector('[data-plus]');
      const minus = benefitBtn.querySelector('[data-minus]');
      const highlight = benefitBtn.querySelector('[data-highlight]');
      benefitBtn.classList.add('tw-font-semibold');
      if (plus) {
        plus.classList.add('!tw-hidden');
      }
      if (minus) {
        minus.classList.add('!tw-block');
      }
      if (highlight) {
        highlight.classList.add('!tw-block');
      }
    });
  }

  // Handle mobile scrolling with debouncing only for scroll operations
  async handleMobileScrollWithDebounce(benefitName) {
    // Debounce only scroll operations, not UI updates
    if (this.isScrolling) {
      return;
    }
    this.isScrolling = true;

    await this.handleMobileScroll(benefitName);
  }

  getBenefitButtons(list, benefitName) {
    if (!list) {
      return { active: null, previous: null, next: null };
    }

    const active = list.querySelector(`[data-benefit="${benefitName}"]`);
    if (!active) {
      return { active: null, previous: null, next: null };
    }

    return {
      active,
      previous: this.findPreviousBenefitButton(active),
      next: this.findNextBenefitButton(active),
    };
  }

  scrollList(wrapperSelector, benefitName) {
    const list = this.getSubheadingList(wrapperSelector);
    const { active, previous, next } = this.getBenefitButtons(list, benefitName);

    if (!list || !active) {
      return { list, active, scrolled: false };
    }

    const reference = previous || active;
    const listRect = list.getBoundingClientRect();
    const referenceRect = reference.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const nextRect = next ? next.getBoundingClientRect() : null;
    const requiresScroll =
      referenceRect.top < listRect.top - VISIBILITY_TOLERANCE_PX ||
      activeRect.bottom > listRect.bottom + VISIBILITY_TOLERANCE_PX ||
      (nextRect !== null && nextRect.bottom > listRect.bottom + VISIBILITY_TOLERANCE_PX);

    if (requiresScroll) {
      const target = Math.max(this.calculateScrollToTop(reference, list), 0);
      const startTop = list.scrollTop;
      const distance = target - startTop;
      if (Math.abs(distance) < VISIBILITY_TOLERANCE_PX) {
        return { list, active, scrolled: false };
      }
      this.animateScroll(list, startTop, target, LIST_SCROLL_DURATION_MS);
      return { list, active, scrolled: true };
    }

    return { list, active, scrolled: false };
  }

  animateScroll(element, start, end, duration) {
    const startTime = performance.now();

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = this.easeOutQuad(progress);
      element.scrollTop = start + (end - start) * eased;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }

  easeOutQuad(t) {
    return t * (2 - t);
  }

  // Handle mobile scrolling (page scroll + single list scroll)
  async handleMobileScroll(benefitName) {
    const { active } = this.scrollList('[data-subheadings-mobile]', benefitName);

    // Always do page scroll to position benefits section below header
    const header = document.querySelector('store-header');
    const headerHeight = header ? header.offsetHeight : 0;
    const benefitWrapper = this.querySelector('.benefit-wrapper');
    
    if (benefitWrapper) {
      const rect = benefitWrapper.getBoundingClientRect();
      // Position wrapper directly below header without extra buffer
      const scrollY = window.scrollY + rect.top - headerHeight;
      window.scrollTo({ top: scrollY, behavior: 'smooth' });
    }

    // Reset scroll flag
    setTimeout(() => {
      this.isScrolling = false;
    }, MOBILE_SCROLL_RESET_MS);
  }

  // Handle desktop scrolling with debouncing only for scroll operations
  handleDesktopScrollWithDebounce(benefitName) {
    // Debounce only scroll operations, not UI updates
    if (this.isScrolling) {
      return;
    }
    this.isScrolling = true;

    this.handleDesktopScroll(benefitName);
  }

  // Handle desktop scrolling (list scroll only)
  handleDesktopScroll(benefitName) {
    this.scrollList('[data-subheadings-desktop]', benefitName);

    // Reset scroll flag
    setTimeout(() => {
      this.isScrolling = false;
    }, DESKTOP_SCROLL_RESET_MS);
  }

}

customElements.define('product-benefits', ProductBenefits);
